import React, { useMemo, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { useCurrentUser } from '../../hooks/useCurrentUser'
import { getUserMenuItems } from '../../lib/userMenu'
import Icon from '../icon'
import LegalModal from './LegalModal'
import styles from './SettingsNav.module.scss'

/**
 * Left rail for the settings/account area.
 *
 * Items and their gating come from lib/userMenu.js — the same source the navbar
 * dropdown uses — so this rail always reflects exactly what the signed-in user
 * can reach. Destinations that don't exist as routes simply aren't in that list.
 */
export default function SettingsNav() {
  const { t } = useTranslation()
  const { currentUser, hasBetaFeature, locale } = useCurrentUser()
  const { pathname, hash } = useLocation()
  const [legalOpen, setLegalOpen] = useState(false)

  const groups = useMemo(
    () => getUserMenuItems({ currentUser, hasBetaFeature, locale, t }),
    [currentUser, hasBetaFeature, locale, t],
  )

  const currentPath = `${pathname}${hash}`

  const renderItem = item => {
    const isActive = item.to === currentPath || item.to === pathname
    const className = `${styles.item} ${isActive ? styles.itemActive : ''}`

    const inner = (
      <>
        <Icon name={item.icon} className={styles.icon} />
        <span>{item.label}</span>
      </>
    )

    if (item.action === 'legal') {
      return (
        <button
          key={item.id}
          type="button"
          className={className}
          onClick={() => setLegalOpen(true)}
        >
          {inner}
        </button>
      )
    }

    if (item.external) {
      return (
        <a
          key={item.id}
          href={item.href}
          target="_blank"
          rel="noreferrer"
          className={className}
        >
          {inner}
        </a>
      )
    }

    return (
      <Link
        key={item.id}
        to={item.to}
        className={className}
        aria-current={isActive ? 'page' : undefined}
      >
        {inner}
      </Link>
    )
  }

  return (
    <>
      <nav
        className={styles.nav}
        aria-label={t('settings.nav.label', 'Account navigation')}
      >
        {groups.map(group => (
          <div key={group.id} className={styles.group}>
            {group.items.map(renderItem)}
          </div>
        ))}
      </nav>
      <LegalModal show={legalOpen} onHide={() => setLegalOpen(false)} />
    </>
  )
}
